// src/redux/attorneyDataSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authenticatedFetch } from "../network/api";

export const fetchAttorney = createAsyncThunk(
  "attorneyData/fetchAttorney",
  async (userId) => {
    const response = await fetch(
      `/api/attorney/fetchAttorneyData?userId=${userId}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch attorney data");
    }
    const data = await response.json();
    return data;
  }
);

export const fetchAttorneys = createAsyncThunk(
  "attorneyData/fetchAttorneys",
  async (firm_id) => {
    const response = await fetch(
      `/api/attorney/fetchFirmMembers?firm_id=${firm_id}`
    );
    if (!response.ok) {
      console.error("Error fetching attorneys from API");
      throw new Error("Error fetching attorneys from API");
    }
    const data = await response.json();
    return data;
  }
);

export const updateAttorneyUserData = createAsyncThunk(
  "attorneyData/updateAttorneyUserData",
  async (updatedData) => {
    const response = await authenticatedFetch(
      "/api/attorney/webapp/updateAttorneyUserData",
      {
        method: "POST",
        body: JSON.stringify(updatedData),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to update attorney data");
    }
    const data = await response.json();
    return data;
  }
);

// Fetch Stream token
export const fetchToken = createAsyncThunk(
  "attorneyData/fetchToken",
  async (userId) => {
    const response = await fetch("/api/generateStreamToken", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stream_id: userId }),
    });

    const { token, expiresAt, generatedAt } = await response.json();
    if (!token) {
      throw new Error("Failed to generate Stream token");
    }
    return { token, expiresAt, generatedAt };
  }
);

export const fetchVideoToken = createAsyncThunk(
  "attorneyData/fetchVideoToken",
  async (userId) => {
    const response = await fetch("/api/generateStreamVideoToken", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stream_id: userId }),
    });

    const responseJson = await response.json();
    const { videoToken, expiresAt, generatedAt } = responseJson;

    if (!videoToken) {
      throw new Error("Failed to generate Stream token");
    }
    return { videoToken, expiresAt, generatedAt };
  }
);

export const fetchOnboardingStage = createAsyncThunk(
  "attorneyData/fetchOnboardingStage",
  async (userId) => {
    const response = await authenticatedFetch(
      `/api/attorney/webapp/fetchOnboardingStage?user_id=${userId}`
    );
    if (!response.ok) {
      throw new Error("Failed to fetch onboarding stage");
    }
    const data = await response.json();
    return data.onboarding_stage;
  }
);

const initialState = {
  attorneyData: null,
  status: "idle", // idle | loading | succeeded | failed
  token: null,
  videoToken: null,
  tokenExpiresAt: null,
  tokenGeneratedAt: null,
  videoTokenExpiresAt: null,
  videoTokenGeneratedAt: null,
  attorneys: [],
  error: null,
};

const attorneyDataSlice = createSlice({
  name: "attorneyData",
  initialState,
  reducers: {
    resetAttorneyData: () => initialState, // Action to reset the state,

    updateAttorneyData: (state, action) => {
      state.attorneyData = action.payload; // Update the client data with the payload
    },

    // Action to update the status
    updateStatus: (state, action) => {
      state.status = action.payload; // Set the status to the value passed in payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttorney.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAttorney.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attorneyData = action.payload;
      })
      .addCase(fetchAttorney.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      .addCase(updateAttorneyUserData.fulfilled, (state, action) => {
        state.attorneyData = action.payload; // Update the state with the updated data
      })
      .addCase(updateAttorneyUserData.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.tokenExpiresAt = action.payload.expiresAt;
        state.tokenGeneratedAt = action.payload.generatedAt;
      })
      .addCase(fetchToken.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchVideoToken.fulfilled, (state, action) => {
        state.videoToken = action.payload.videoToken;
        state.videoTokenExpiresAt = action.payload.expiresAt;
        state.videoTokenGeneratedAt = action.payload.generatedAt;
      })
      .addCase(fetchVideoToken.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchOnboardingStage.fulfilled, (state, action) => {
        // Assuming the onboarding stage is stored in the attorneyData slice
        // This might need adjustment based on how the onboarding stage is managed
        // For now, we'll just update the state with the onboarding stage
        if (state.attorneyData) {
          state.attorneyData = { ...state.attorneyData, onboarding_stage: action.payload };
        } else {
          state.attorneyData = { onboarding_stage: action.payload };
        }
      })
      .addCase(fetchOnboardingStage.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchAttorneys.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAttorneys.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.attorneys = action.payload;
      })
      .addCase(fetchAttorneys.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const { resetAttorneyData, updateAttorneyData, updateStatus } =
  attorneyDataSlice.actions;

export default attorneyDataSlice.reducer;
